import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const NPI_LOOKUP_FETCH_TIMEOUT_MS = 120000;

async function fetchWithTimeout(input: string, init?: RequestInit): Promise<Response> {
  const raw = { ...(init ?? {}) } as Record<string, unknown>;
  const timeoutMs =
    typeof raw.timeoutMs === "number" ? raw.timeoutMs : NPI_LOOKUP_FETCH_TIMEOUT_MS;
  delete raw.timeoutMs;
  const rest = raw as RequestInit;
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (e) {
    if (timedOut) {
      throw new Error(
        `Request timed out after ${Math.round(timeoutMs / 1000)}s. Try again or check your network.`,
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Keep local to avoid pulling the full countries stack into this thin client module. */
function assertUnitedStatesNpiRegistry(country_code: string | undefined): void {
  const c = (country_code ?? "").trim().toUpperCase();
  if (!c || c === "USA" || c === "US") {
    return;
  }
  throw new Error(
    "US NPI registry lookup is only for United States facilities. Use facility search by name for Canada and other countries.",
  );
}

// NPI Lookup Service
// Note: NPI is now optional - users can search by organization name instead
export interface NPILookupPayload {
  npi_number: string;
  // Optional filters to help narrow down the NPI search
  city?: string;
  state?: string;
  /** ISO country (e.g. CAN, USA); rules API skips US NPPES when not USA/US. */
  country_code?: string;
}

export interface NPIOrganizationLookupPayload {
  organization_name: string;
  city?: string;
  state?: string;
  /** Selected country from UI (e.g. CAN, USA); required for correct non-US facility search. */
  country_code?: string;
  /** When true, API omits NPIs already linked to another external user's verified facility (e.g. signup). */
  exclude_associated_facilities?: boolean;
}

// Extended result interface for organization lookup (includes taxonomy_match flag)
export interface NPIOrganizationResult extends NPIResult {
  taxonomy_match?: boolean; // Whether the facility type was matched in our database
}

export interface NPIBasicInfo {
  organization_name?: string;
  organizational_subpart?: string;
  parent_organization_legal_business_name?: string;
  enumeration_date?: string;
  last_updated?: string;
  certification_date?: string;
  status?: string;
  authorized_official_first_name?: string;
  authorized_official_last_name?: string;
  authorized_official_telephone_number?: string;
  authorized_official_title_or_position?: string;
}

export interface NPIAddress {
  country_code?: string;
  country_name?: string;
  address_purpose?: string;
  address_type?: string;
  address_1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  telephone_number?: string;
  fax_number?: string;
}

export interface NPITaxonomy {
  code?: string;
  taxonomy_group?: string;
  desc?: string;
  state?: string;
  license?: string;
  primary?: boolean;
}

export interface NPIOtherName {
  organization_name?: string;
  type?: string;
  code?: string;
}

export interface NPIPrimaryAddress {
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  telephone_number?: string;
  fax_number?: string;
}

export interface NPIPrimaryTaxonomy {
  code?: string;
  description?: string;
  state?: string;
  license?: string;
}

export interface NPIFacilityType {
  id?: number;
  type_slug?: string;
  display_name?: string;
  category?: string;
  code?: string;
  grouping?: string | null;
  classification?: string | null;
  specialization?: string | null;
}

export interface NPIResult {
  npi_number?: string;
  enumeration_type?: string;
  created_epoch?: string;
  last_updated_epoch?: string;
  basic?: NPIBasicInfo;
  addresses?: NPIAddress[];
  taxonomies?: NPITaxonomy[];
  identifiers?: any[];
  endpoints?: any[];
  other_names?: NPIOtherName[];
  /** Display name from NPPES: DBA when present, else legal name (see unicomply-rules npi_lookup_service). */
  site_name?: string;
  organization_name?: string;
  first_name?: string;
  last_name?: string;
  enumeration_date?: string;
  last_updated?: string;
  status?: string;
  primary_address?: NPIPrimaryAddress;
  primary_taxonomy?: NPIPrimaryTaxonomy;
  facility_type?: NPIFacilityType;
  /** True only when a single facility type was determined from taxonomy; false when ambiguous or from name match. */
  facility_type_confirmed?: boolean;
  /** When true, NPI has practitioner/group primary taxonomy and POS did not resolve; user must select facility type. */
  facility_type_requires_selection?: boolean;
  /** Default slug to suggest when facility_type_requires_selection is true (e.g. rural_health_clinic). */
  default_facility_type_slug?: string;
  /** CCN from CMS POS when facility type was resolved via POS (e.g. RHC/FQHC). */
  ccn?: string;
  /** True when this facility is already registered by another user. Cards should be greyed out and non-interactive. */
  already_registered?: boolean;
}

/** LLM-suggested facility when NPI org lookup has no exact match (success: false) */
export interface NPILookupLLMSuggestion {
  site_name: string;
  city: string;
  state: string;
  country: string;
  facility_url?: string;
  facility_type: string;
  facility_type_id?: number;
  category?: string;
  similarity_score?: number;
  /** Set when NPPES enrichment links this suggestion to a registry row */
  npi_number?: string;
  organization_name?: string;
  primary_address?: NPIPrimaryAddress;
  primary_taxonomy?: NPIPrimaryTaxonomy;
  addresses?: NPIAddress[];
  ccn?: string;
  ccn_number?: string;
  already_registered?: boolean;
}

export interface NPILookupData {
  result_count?: number;
  results?: NPIResult[];
  /** Present when organization lookup finds no NPI match but LLM returned suggestions */
  llm_suggestions?: NPILookupLLMSuggestion[];
}

export interface NPILookupResponse {
  success: boolean;
  message: string;
  data: NPILookupData | null;
  error?: { code?: string; message?: string } | null;
}

class NPILookupService {
  
  private baseURL = getApiOrigin();
  async lookupNPI(payload: NPILookupPayload): Promise<NPILookupResponse> {
    try {
      console.log("🔍 Looking up NPI:", payload.npi_number);

      const token = getValidAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const body: Record<string, unknown> = {
        npi_number: payload.npi_number,
      };
      if ((payload.city ?? "").trim()) body.city = (payload.city ?? "").trim();
      if ((payload.state ?? "").trim()) body.state = (payload.state ?? "").trim().toUpperCase();
      if ((payload.country_code ?? "").trim()) {
        body.country_code = (payload.country_code ?? "").trim();
      }

      assertUnitedStatesNpiRegistry(payload.country_code);

      const response = await fetchWithTimeout(`${this.baseURL}/api/v1/npi/lookup/number`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      check401(response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NPILookupResponse = await response.json();

      console.log("✅ NPI lookup response:", data);

      return data;
    } catch (error) {
      console.error("❌ NPI lookup failed:", error);
      throw error;
    }
  }

  async lookupOrganization(payload: NPIOrganizationLookupPayload): Promise<NPILookupResponse> {
    try {
      console.log("🔍 Looking up organization:", payload.organization_name);

      const token = getValidAccessToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const body: Record<string, unknown> = {
        organization_name: payload.organization_name,
      };
      if (payload.exclude_associated_facilities != null) {
        body.exclude_associated_facilities = payload.exclude_associated_facilities;
      }
      const city = (payload.city ?? "").trim();
      const state = (payload.state ?? "").trim();
      if (city) body.city = city;
      if (state) body.state = state.toUpperCase();
      if ((payload.country_code ?? "").trim()) {
        body.country_code = (payload.country_code ?? "").trim();
      }

      assertUnitedStatesNpiRegistry(payload.country_code);

      const response = await fetchWithTimeout(`${this.baseURL}/api/v1/npi/lookup/organization`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      check401(response);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: NPILookupResponse = await response.json();

      console.log("✅ Organization lookup response:", data);

      return data;
    } catch (error) {
      console.error("❌ Organization lookup failed:", error);
      throw error;
    }
  }
}

export const npiLookupService = new NPILookupService();